import React, { ActionDispatch, FC, useContext, useEffect } from "react";
import { DragAndDropMultiFileUploader, sortStringListOrderAscending, TooltipWrapper } from "@app/shared";
import { ResultRegion, SpecialtyGroupExceptionStatus } from "@app/projects/results/common";
import { QESCheckbox, QESSelect, QESTextArea } from "@app/shared/qes-standards";
import {
  DragAndDropMultiFileUploaderContext,
  DragAndDropMultiFileUploaderContextProvider
} from "@app/shared/DragAndDropMultiFileUploaderContext";

import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import SpecialtyGroupException, {
  SpecialtyGroupExceptionGrade,
  SupplementaryDocument
} from "@app/projects/results/common/specialtyGroupException";
import { RoleRestricted, Roles, UserContext } from "@app/user";
import ExceptionsService from "@app/exceptions/exceptionsService";
import { authenticatedDownload } from "@app/shared/AuthenticatedDownload";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { some, uniqueId } from "lodash";
import moment from "moment/moment";
import cx from "classnames";
import {
  EditExceptionForm,
  EditExceptionFormAction,
  EditExceptionFormActionType
} from "@app/exceptions/editExceptionReducer";

type SpecialtyGroupExceptionPanelProps = {
  exception: SpecialtyGroupException;
  expandedColumnSizes?: boolean;
  formState: EditExceptionForm;
  region: ResultRegion;
  formDispatch: ActionDispatch<[action: EditExceptionFormAction]>;
  specialtyGroupCode: string;
  specialtyGroupName: string;
};

export const getInitialFormState = (exception: SpecialtyGroupException): EditExceptionForm => {
  const unarchivedExceptionToLoad = exception?.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.Archived ? undefined : exception;
  const defaultStatus = !unarchivedExceptionToLoad || unarchivedExceptionToLoad && (unarchivedExceptionToLoad.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.None || unarchivedExceptionToLoad.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.Archived)
    ? SpecialtyGroupExceptionStatus.Pending
    : unarchivedExceptionToLoad.specialtyGroupExceptionStatus;
  const startDate = unarchivedExceptionToLoad && unarchivedExceptionToLoad.startDate ? moment(unarchivedExceptionToLoad.startDate).format("YYYY-MM-DD") : '---';
  const endDate = unarchivedExceptionToLoad && unarchivedExceptionToLoad.endDate ? moment(unarchivedExceptionToLoad.endDate).format("YYYY-MM-DD") : '---';
  const originalStartDate = startDate !== '---' ? startDate : '';
  const originalEndDate = endDate !== '---' ? endDate : '';
  return {
    comment: unarchivedExceptionToLoad && unarchivedExceptionToLoad.comment || "",
    endDate: originalEndDate,
    grade: unarchivedExceptionToLoad && unarchivedExceptionToLoad.grade || SpecialtyGroupExceptionGrade.None,
    narrative: unarchivedExceptionToLoad && unarchivedExceptionToLoad.exceptionNarrative || "",
    originalComment: unarchivedExceptionToLoad && unarchivedExceptionToLoad.comment || "",
    originalEndDate: originalEndDate,
    originalGrade: unarchivedExceptionToLoad && unarchivedExceptionToLoad.grade || SpecialtyGroupExceptionGrade.None,
    originalNarrative: unarchivedExceptionToLoad && unarchivedExceptionToLoad.exceptionNarrative || "",
    originalPreviouslyApproved: unarchivedExceptionToLoad && unarchivedExceptionToLoad.previouslyApproved || false,
    originalStartDate: originalStartDate,
    originalStatus: defaultStatus,
    originalUploadedDocuments: unarchivedExceptionToLoad && unarchivedExceptionToLoad.supplementaryDocuments || [],
    previouslyApproved: unarchivedExceptionToLoad && unarchivedExceptionToLoad.previouslyApproved || false,
    startDate: originalStartDate,
    status: defaultStatus,
    uploadedDocuments: !!exception ? exception.supplementaryDocuments : [],
    validEndDate: true,
    validStartDate: true
  }
}

export const mergeFormAndException = (exception: SpecialtyGroupException, state: EditExceptionForm, region: ResultRegion, specialtyGroupCode: string): SpecialtyGroupException => {
  const result = !!exception
    ? {
      ...exception,
      comment: state.comment,
      endDate: state.endDate ? moment(state.endDate).toDate() : undefined,
      exceptionNarrative: state.narrative,
      grade: state.grade,
      previouslyApproved: state.previouslyApproved,
      specialtyGroupExceptionStatus: state.status,
      startDate: state.startDate ? moment(state.startDate).toDate() : undefined,
      supplementaryDocuments: state.uploadedDocuments,
    }
    : {
      ...region,
      comment: state.comment,
      endDate: state.endDate ? moment(state.endDate).toDate() : undefined,
      exceptionNarrative: state.narrative,
      grade: state.grade,
      previouslyApproved: state.previouslyApproved,
      specialtyGroupCode: specialtyGroupCode,
      specialtyGroupExceptionStatus: state.status,
      startDate: state.startDate ? moment(state.startDate).toDate() : undefined,
      supplementaryDocuments: state.uploadedDocuments,
    };
  return result as SpecialtyGroupException;
}

const WrappedSpecialtyGroupExceptionPanel: FC<React.PropsWithChildren<SpecialtyGroupExceptionPanelProps>> = ({
                                                                                                               exception,
                                                                                                               expandedColumnSizes,
                                                                                                               specialtyGroupName,
                                                                                                               region,
                                                                                                               formState,
                                                                                                               formDispatch
                                                                                                             }: SpecialtyGroupExceptionPanelProps) => {
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const multiFileUploaderContext = useContext(DragAndDropMultiFileUploaderContext)
  const userContext = useContext(UserContext);
  const uploadDocumentsApiKey: string = "document";
  const exceptionsService = new ExceptionsService();

  const statusId = uniqueId("specialty-group-exception-panel-status-");
  const commentsId = uniqueId("specialty-group-exception-panel-comments-");
  const exceptionNarrativeId = uniqueId("specialty-group-exception-panel-exception-narrative-");
  const gradeContainerClasses = "col-5 mb-3 mt-3 pr-0 exceptions__grade-control-container";
  const disableGradeEdits = !userContext.roles.includes(Roles.SPECIALTY_EXCEPTION_REVIEWER_ADMIN);

  useEffect(() => {
    const initialState = getInitialFormState(exception);
    formDispatch({ payload: initialState, type: EditExceptionFormActionType.Reset });
    if (!exception) {
      multiFileUploaderContext.setUploadedFiles(new Map());
      return;
    }
    exception.supplementaryDocuments.forEach(item => {
      multiFileUploaderContext.setUploadedFiles(prevFiles => new Map(prevFiles).set(item.supplementaryDocumentMetadataId, new File([], item.documentName)));
    });
  }, [exception])

  const handleDownload = async (metadataId: number) => {
    const link = (!!metadataId && metadataId > 0) ?
      `${exceptionsService.downloadSupplementaryDocument(metadataId)}` : `#`;
    authenticatedDownload(link);
  };

  const handleFileUpload = async (file: File) => {

    return new Promise<void>(async (resolve, reject) => {
      try {
        const formData = new FormData();
        formData.append(uploadDocumentsApiKey, file);
        const metadataId = await exceptionsService.uploadSupplementaryDocument(formData);
        const fileinfo: FormDataEntryValue = formData.get(uploadDocumentsApiKey);
        if (!!metadataId && fileinfo instanceof File) {
          const supplementaryDocument: SupplementaryDocument = {
            documentName: fileinfo.name ? fileinfo.name : '',
            supplementaryDocumentMetadataId: metadataId
          }
          formDispatch({ payload: [supplementaryDocument], type: EditExceptionFormActionType.AddUploadedDocuments })
          multiFileUploaderContext.setUploadedFiles(prevFiles => new Map(prevFiles).set(metadataId, file));
          resolve();
        }
      } catch (error) {
        reject(error.responseJSON);
      }

    });
  };

  const shouldShowStatus = (value: SpecialtyGroupExceptionStatus) => {
    const statuses = [
      SpecialtyGroupExceptionStatus.Pending,
      SpecialtyGroupExceptionStatus.Submitted,
      SpecialtyGroupExceptionStatus.Approved,
      SpecialtyGroupExceptionStatus.Denied,
      SpecialtyGroupExceptionStatus.Appealed,
    ];
    return some(statuses, (s) => s === value);
  }

  return (
    <>
      <p>
        Please enter the current status of the Specialty Exception process
        for <strong>{specialtyGroupName}</strong> in <strong>{region.displayName}</strong> county...
      </p>

      <div className="row">
        <div className={cx({ "col-3": !expandedColumnSizes, "col-5": expandedColumnSizes, "mb-3": true })}>
          <QESSelect
            id={statusId}
            className="form-control"
            value={formState.status}
            onChange={e => formDispatch({ payload: e.target.value as SpecialtyGroupExceptionStatus, type: EditExceptionFormActionType.Status })}
            label="Status this year"
            testId="exception-panel__status-dropdown"
          >
            {Object.keys(SpecialtyGroupExceptionStatus).sort((a: string, b: string) => {
              return sortStringListOrderAscending(a, b);
            }).map((status) => {
              const value: SpecialtyGroupExceptionStatus = (SpecialtyGroupExceptionStatus)[status as keyof typeof SpecialtyGroupExceptionStatus];
              if (!shouldShowStatus(value)) {
                return null;
              }

              return (
                <option key={value} value={value}>{status}</option>
              );
            })}
          </QESSelect>
        </div>
      </div>

      <div>
        <QESDateInput
          containerClassName="mt-3 col-5 p-0"
          errorText="Start Date must be before Expiration Date"
          isValid={formState.validStartDate}
          label="Start Date"
          testId="exception-panel__start-date"
          onChange={(e) => formDispatch({ payload: e.target.value, type: EditExceptionFormActionType.StartDate })}
          unrequiredValidation={true}
          value={formState.startDate}
        />

        <QESDateInput
          containerClassName="mt-3 col-5 p-0"
          errorText="Expiration Date must be after Start Date"
          isValid={formState.validEndDate}
          label="Expiration Date"
          testId="exception-panel__end-date"
          onChange={(e) => formDispatch({ payload: e.target.value, type: EditExceptionFormActionType.EndDate })}
          unrequiredValidation={true}
          value={formState.endDate}
        />
      </div>

      <div className="row">
        <TooltipWrapper
          text={disableGradeEdits ? 'Users with sufficient permissions may edit the Exception Grade.' : null}
          containerClassName={disableGradeEdits ? gradeContainerClasses : 'w-100'}
          triggerWidth="100%">
          <div
            className={disableGradeEdits ? '' : gradeContainerClasses}>
            <TooltipWrapper
              text={`A = Exception has been reviewed, has all necessary documentation attached and is ready for submission.
B = Exception needs some work around the narrative and/or reviewing additional providers before submission.\n
C = Exception is missing necessary supporting documentation, narrative, and/or excluded providers.`}>
              <i className="fs14 fa fa-info-circle"/>
            </TooltipWrapper>
            <QESSelect
              className="form-control"
              value={formState.grade}
              onChange={e => formDispatch({ payload: e.target.value as SpecialtyGroupExceptionGrade, type: EditExceptionFormActionType.Grade })}
              label="Exception Grade"
              testId="exception-panel__grade-dropdown"
              disabled={disableGradeEdits}
            >
              {Object.keys(SpecialtyGroupExceptionGrade).map((grade) => {
                const value: SpecialtyGroupExceptionGrade = (SpecialtyGroupExceptionGrade)[grade as keyof typeof SpecialtyGroupExceptionGrade];
                if (value === SpecialtyGroupExceptionGrade.None) {
                  return (
                    <option key={value} value={value}>---</option>
                  );
                }
                return (
                  <option key={value} value={value}>{grade}</option>
                );
              })}
            </QESSelect>
          </div>
        </TooltipWrapper>
      </div>

      <div className="row">
        <div className="col-12 mb-3">
          <div className="checkbox pt0-imp">
            <QESCheckbox label="Previously Approved" testId="exception-panel__previously-approved"
                         checked={formState.previouslyApproved}
                         onChange={e => formDispatch({ payload: e.target.checked, type: EditExceptionFormActionType.PreviouslyApproved })}/>
          </div>
        </div>
      </div>

      <div className="row">
        <div className={cx({ "col-6": !expandedColumnSizes, "col-9": expandedColumnSizes })}>
          <QESTextArea
            label="Comments"
            maxLength={4000}
            rows={4}
            id={commentsId}
            value={formState.comment}
            onChange={e => formDispatch({ payload: e.target.value, type: EditExceptionFormActionType.Comment })}
            testId="exception-panel__comment"
          />
        </div>
      </div>

      <div className="row">
        <div className={cx({ "col-6": !expandedColumnSizes, "col-9": expandedColumnSizes })}>
          <QESTextArea
            label="Exception Narrative"
            maxLength={4000}
            rows={4}
            id={exceptionNarrativeId}
            value={formState.narrative}
            onChange={e => formDispatch({ payload: e.target.value, type: EditExceptionFormActionType.Narrative })}
            testId="exception-panel__exception-narrative"
          />
        </div>
      </div>

      <div className="row">
        <div className={cx({ "col-6": !expandedColumnSizes, "col-9": expandedColumnSizes })}>
          <RoleRestricted allowedRoles={[Roles.EXCEPTION_MANAGER]}>
            {featureFlags.has(Features.VOLUME_EXCEPTIONS_SUPPLEMENTARY_DOCUMENTS) &&
              <DragAndDropMultiFileUploader
                apiKeyName={uploadDocumentsApiKey}
                title="Attach Supplemental Documents. Attachments must be in .doc, .docx, .png, .jpg, or .pdf format."
                filetype=".doc,.docx,.png,.jpg,.jpeg,.pdf"
                onMultipleUpload={handleFileUpload}
                handleDownloadLink={handleDownload}
                onRemove={id => formDispatch({ payload: id, type: EditExceptionFormActionType.RemoveUploadedDocumentByKey })}
                small={true}
                testId="edit-project-upload-supplementary-docs"
              />
            }
          </RoleRestricted>
        </div>
      </div>
    </>
  );
}

const SpecialtyGroupExceptionPanel: FC<React.PropsWithChildren<SpecialtyGroupExceptionPanelProps>> = (props: SpecialtyGroupExceptionPanelProps) => {
  return <DragAndDropMultiFileUploaderContextProvider>
    <WrappedSpecialtyGroupExceptionPanel {...props} />
  </DragAndDropMultiFileUploaderContextProvider>;
}

export default SpecialtyGroupExceptionPanel;